import { FC, useState, useCallback } from "react";
import { Typography, Stack, Box, Collapse, Tooltip, Popover } from "@mui/material";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { InnerBox } from "@/components/elements/InnerBox";
import { divider, black, blue, background } from "@/theme/colors";
import { rounded, elevation } from "@/theme/variables";
import { Talent, TCaseScheduleItem, InterviewMethod, MeetingToolType } from "@/features/case/types/case";
import { CaseInfoItem } from "@/features/case/components/CaseBasicInfo/CaseInfoItem";
import { dayjs } from "@/utils/dayjs";
import { DATE_FORMAT } from "@/constants/datetime";
import { Tag } from "@/components/elements/Tag";
import { text } from "@/theme/colors";
import { truncateWithEllipsis } from "@/utils/string";
import { IconBox } from "@/components/elements/IconBox";
import { IconCopy } from "@/assets/icons/IconCopy";

type SharedCaseSchedulesProps = {
  caseSchedules: TCaseScheduleItem[];
};

const MAX_VISIBLE_TALENTS = 5;

/** Read-only schedule list for shared (public) view */
export const SharedCaseSchedules: FC<SharedCaseSchedulesProps> = ({ caseSchedules }) => {
  const [expandedSchedules, setExpandedSchedules] = useState<Record<number, boolean>>(() => {
    const initial: Record<number, boolean> = {};
    caseSchedules.forEach((schedule) => {
      if (schedule.id) {
        const isPassed =
          schedule.dateConfirm && schedule.startTimeConfirm && dayjs(`${schedule.dateConfirm} ${schedule.startTimeConfirm}`).isBefore(dayjs());
        initial[schedule.id] = !isPassed;
      }
    });
    return initial;
  });

  const toggleScheduleExpand = useCallback((scheduleId: number) => {
    setExpandedSchedules((prev) => ({ ...prev, [scheduleId]: !prev[scheduleId] }));
  }, []);

  // Talent overflow popover state
  const [talentPopoverAnchor, setTalentPopoverAnchor] = useState<HTMLElement | null>(null);
  const [popoverTalents, setPopoverTalents] = useState<Talent[]>([]);

  const handleTalentOverflowClick = useCallback((event: React.MouseEvent<HTMLElement>, talents: Talent[]) => {
    event.stopPropagation();
    setTalentPopoverAnchor(event.currentTarget);
    setPopoverTalents(talents);
  }, []);

  const handleTalentPopoverClose = useCallback(() => {
    setTalentPopoverAnchor(null);
    setPopoverTalents([]);
  }, []);

  const handleCopySchedule = useCallback((schedule: TCaseScheduleItem, event: React.MouseEvent) => {
    event.stopPropagation();
    const parts: string[] = [];
    if (schedule.dateConfirm && schedule.startTimeConfirm) {
      parts.push("【面接日時】");
      parts.push(`${schedule.dateConfirm} ${schedule.startTimeConfirm}`);
    }
    // Include the matched candidates so the copied text states who the interview is for (one talent per line).
    if (schedule.talents?.length) {
      if (parts.length > 0) parts.push("");
      parts.push("【マッチング人材】");
      schedule.talents.forEach((talent) => parts.push(talent.fullName));
    }
    if (schedule.url) {
      if (parts.length > 0) parts.push("");
      parts.push("【面接URL】");
      parts.push(schedule.url);
    }
    navigator.clipboard.writeText(parts.join("\n"));
  }, []);

  const isSchedulePassed = (schedule: TCaseScheduleItem): boolean => {
    if (!schedule.dateConfirm || !schedule.startTimeConfirm) return false;
    const startDateTime = dayjs(`${schedule.dateConfirm} ${schedule.startTimeConfirm}`);
    return startDateTime.isValid() && startDateTime.isBefore(dayjs());
  };

  const getInterviewMethodLabel = (method: number | null | undefined): string => {
    switch (method) {
      case InterviewMethod.ONLINE:
        return "オンライン";
      case InterviewMethod.OFFLINE:
        return "対面";
      default:
        return "";
    }
  };

  const getMeetingToolLabel = (toolType: number | null | undefined): string => {
    switch (toolType) {
      case MeetingToolType.ZOOM:
        return "Zoom";
      case MeetingToolType.TEAMS:
        return "Microsoft Teams";
      case MeetingToolType.MEET:
        return "Google Meet";
      default:
        return "";
    }
  };

  const hasCopyableData = (schedule: TCaseScheduleItem): boolean => {
    return !!(schedule.dateConfirm && schedule.startTimeConfirm) || !!schedule.url;
  };

  return (
    <InnerBox
      mt={4}
      mb={3}
      p={3}
      showDivider={false}
      borderColor={divider.high}
      bordered={true}
      title={
        <Typography variant="sub16Bold" color={black[30]}>
          日程調整
        </Typography>
      }
    >
      {caseSchedules.map((schedule, index) => {
        const dateFormat = schedule.dateConfirm ? dayjs(schedule.dateConfirm).format(DATE_FORMAT) : null;
        const displayDate = dateFormat || schedule.dateConfirm;
        const hasConfirmedDateTime = !!(displayDate && schedule.startTimeConfirm);
        const confirmedDateTime = hasConfirmedDateTime ? `${displayDate} ${schedule.startTimeConfirm}` : null;
        const isExpanded = schedule.id ? (expandedSchedules[schedule.id] ?? true) : true;
        const allTalents = schedule?.talents || [];
        const scheduleTalents = allTalents.slice(0, MAX_VISIBLE_TALENTS);
        const overflowTalents = allTalents.slice(MAX_VISIBLE_TALENTS);
        const overflowCount = overflowTalents.length;
        const isPassed = isSchedulePassed(schedule);

        const getInterviewMethodWithTool = () => {
          if (!schedule.interviewMethod) return null;
          const methodLabel = getInterviewMethodLabel(schedule.interviewMethod);
          if (Number(schedule.interviewMethod) === InterviewMethod.ONLINE && schedule.meetingToolType) {
            return `${methodLabel}（${getMeetingToolLabel(schedule.meetingToolType)}）`;
          }
          return methodLabel;
        };

        const getMeetingIdPasscode = () => {
          if (Number(schedule.interviewMethod) !== InterviewMethod.ONLINE) return null;
          const parts = [];
          if (schedule.meetingId) parts.push(schedule.meetingId);
          if (schedule.passcode) parts.push(schedule.passcode);
          const meetingInfo = parts.length > 0 ? parts.join("／") : null;
          return meetingInfo ? truncateWithEllipsis(meetingInfo, 30) : null;
        };

        return (
          <InnerBox mt={2} p={0} showDivider={false} borderColor={divider.high} bordered={true} key={schedule.id ?? index}>
            {/* Accordion Header */}
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ px: 2, py: 1.5, cursor: "pointer" }}
              onClick={() => schedule.id && toggleScheduleExpand(schedule.id)}
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography variant="body14Bold" color={black[30]}>
                  日程{index + 1}
                </Typography>
                {hasCopyableData(schedule) && (
                  <Tooltip title="日程情報などをコピー" arrow>
                    <span>
                      <IconBox onClick={(e) => handleCopySchedule(schedule, e)} sx={{ p: 0.5 }}>
                        <IconCopy width={18} height={18} color={black[70]} />
                      </IconBox>
                    </span>
                  </Tooltip>
                )}
                {isPassed && <Tag color={black[99]} textColor={text.primary} fontWeight={400} title="面接実施済み" minWidth="auto" />}
              </Stack>
              {isExpanded ? <KeyboardArrowUpIcon sx={{ color: black[70] }} /> : <KeyboardArrowDownIcon sx={{ color: black[70] }} />}
            </Stack>

            {/* Collapsible Content */}
            <Collapse in={isExpanded}>
              <Box sx={{ px: 2, pb: 2 }}>
                {schedule.dateTimeTalent && (
                  <CaseInfoItem label="面接に関する詳細（候補日時、実地面接詳細等）" value={schedule.dateTimeTalent} type="textArea" />
                )}
                {hasConfirmedDateTime && <CaseInfoItem label="面接日時" value={confirmedDateTime} />}
                {scheduleTalents.length > 0 && (
                  <Box sx={{ borderBottom: `1px solid ${divider.high}`, pb: 1.5, mb: 1.5 }}>
                    <Stack mb={1}>
                      <Typography variant="cap12" color={black[70]}>
                        マッチング人材
                      </Typography>
                    </Stack>
                    <Box>
                      {scheduleTalents.map((talent: Talent) => (
                        <Typography key={talent.id} variant="body14" sx={{ display: "block", mb: 0.5 }}>
                          ・{truncateWithEllipsis(talent.fullName, 25)}
                        </Typography>
                      ))}
                      {overflowCount > 0 && (
                        <Typography variant="body14Semi" onClick={(e) => handleTalentOverflowClick(e, overflowTalents)}>
                          他
                          <Typography
                            variant="body14Semi"
                            sx={{
                              color: blue[80],
                              textDecoration: "underline",
                              cursor: "pointer",
                              mt: 0.5,
                            }}
                          >
                            {overflowCount.toString().padStart(2, "0")}
                          </Typography>
                          名
                        </Typography>
                      )}
                    </Box>
                  </Box>
                )}
                {getInterviewMethodWithTool() && (
                  <CaseInfoItem label="実施方法" value={getInterviewMethodWithTool()} isShowDivider={!!(getMeetingIdPasscode() || schedule?.url)} />
                )}
                {getMeetingIdPasscode() && (
                  <CaseInfoItem label="ミーティングID／パスコード" value={getMeetingIdPasscode()} isShowDivider={!!schedule?.url} />
                )}
                {schedule?.url && <CaseInfoItem label="面接URL" type="link" value={schedule?.url} isShowDivider={false} />}
              </Box>
            </Collapse>
          </InnerBox>
        );
      })}
      <Popover
        open={Boolean(talentPopoverAnchor)}
        anchorEl={talentPopoverAnchor}
        onClose={handleTalentPopoverClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        slotProps={{
          paper: {
            sx: {
              mt: 0.5,
              mr: "16px",
              boxShadow: elevation.lv2,
              border: `1px solid ${divider.middle}`,
              borderRadius: rounded.xs,
              maxHeight: 300,
              width: 266,
              overflow: "auto",
            },
          },
        }}
      >
        <Box sx={{ py: 1, px: 0, background: background.white }}>
          {popoverTalents.map((talent) => (
            <Box key={talent.id} sx={{ px: 1.5, py: 0.5 }}>
              <Typography variant="body14">・{truncateWithEllipsis(talent.fullName ?? "", 25)}</Typography>
            </Box>
          ))}
        </Box>
      </Popover>
    </InnerBox>
  );
};
